import React from "react";
import {
	string,
	oneOfType,
	number,
	objectOf,
	object,
	shape,
	array,
	oneOf,
	func,
	arrayOf,
} from "prop-types";
import cx from "classnames";
import { MediaGallery } from "../MediaGallery";
// eslint-disable-next-line import/no-cycle
import { HeroCard } from "../Card";
import * as styles from "./HeroSection.module.scss";

const HeroSection = ({ primaryTile, gallery, otherTiles, className, style, locale }) => (
	<div
		className={cx(
			styles.sectionContainer,
			className,
			gallery ? styles.gallerySectionContainer : ""
		)}
		style={style}>
		{primaryTile && (
			<HeroCard
				key={primaryTile.id}
				className={styles.section}
				headerClassName={styles.header}
				image={primaryTile.image}
				header={primaryTile.header}
				subheader={primaryTile.subheader}
				content={primaryTile.content}
				button={primaryTile.button}
				link={primaryTile.link}
				linkIcon={primaryTile.linkIcon}
				color={primaryTile.color}
				primary
				variant={primaryTile?.variant}
			/>
		)}
		{gallery && (
			<div className={styles.section}>
				<MediaGallery
					gallery={gallery.images}
					logo={gallery?.logo?.fluid}
					logoPosition={gallery?.logoPosition}
					video={
						gallery.video && {
							publicId: gallery.video?.video?.[0]?.public_id,
							cloudName: process.env.GATSBY_CLOUDINARY_CLOUD_NAME,
							type: gallery.video?.type,
							videoId: gallery.video?.videoId,
							videoTitle: gallery.video?.videoTitle,
							autoPlay: gallery.video?.autoPlay,
						}
					}
					map={gallery.map}
					detailMap={gallery.detailMap}
					lang={locale?.substring(0, 2)}
				/>
			</div>
		)}
		{otherTiles?.map(heroCard => (
			<HeroCard
				key={heroCard.id}
				className={styles.section}
				imageClassName={gallery ? "" : styles.otherImage}
				headerClassName={styles.header}
				image={heroCard.image}
				header={heroCard.header}
				subheader={heroCard.subheader}
				content={heroCard.content}
				button={heroCard.button}
				link={heroCard.link}
				linkIcon={heroCard.linkIcon}
				color={heroCard.color}
				slot
			/>
		))}
	</div>
);

HeroSection.propTypes = {
	primaryTile: shape({
		id: string,
		image: shape({
			fluid: oneOfType([object, array]),
			fixed: oneOfType([object, array]),
			source: shape({
				src: string,
				srcSet: string,
				sizes: string,
			}),
			alt: string,
			src: string,
		}).isRequired,
		header: string,
		subheader: string,
		content: string,
		button: shape({
			icon: string,
			iconPlacement: oneOf(["right", "left"]),
			onClick: func,
			text: string,
		}),
		linkIcon: string,
		link: shape({
			to: string.isRequired,
			target: string,
			icon: shape({
				svg: shape({
					content: string,
				}),
			}),
			text: string,
		}),
	}),
	otherTiles: arrayOf(
		shape({
			id: string,
			image: shape({
				fluid: oneOfType([object, array]),
				fixed: oneOfType([object, array]),
				source: shape({
					src: string,
					srcSet: string,
					sizes: string,
				}),
				alt: string,
				src: string,
			}).isRequired,
			header: string,
			subheader: string,
			content: string,
			button: shape({
				icon: string,
				iconPlacement: oneOf(["right", "left"]),
				onClick: func,
				text: string,
			}),
			linkIcon: string,
			link: shape({
				to: string.isRequired,
				target: string,
				icon: shape({
					svg: shape({
						content: string,
					}),
				}),
				text: string,
			}),
		})
	),
	gallery: shape({
		images: arrayOf(
			shape({
				fluid: oneOfType([object, array]),
				fixed: oneOfType([object, array]),
				source: shape({
					src: string,
					srcSet: string,
					sizes: string,
				}),
				alt: string,
				src: string,
			})
		).isRequired,
		logo: arrayOf(object),
		logoPosition: oneOf(["top-left", "top-right", "bottom-left", "bottom-right"]),
		lang: oneOf(["en", "fr"]),
		video: shape({
			video: arrayOf(
				shape({
					public_id: string,
				})
			),
		}),
		map: shape({
			fluid: oneOfType([object, array]),
			fixed: oneOfType([object, array]),
			source: shape({
				src: string,
				srcSet: string,
				sizes: string,
			}),
			alt: string,
			src: string,
		}),
		detailMap: shape({
			zoom: number,
			markers: arrayOf(
				shape({
					name: string,
					longitude: number,
					latitude: number,
					link: shape({ url: string, linkText: string }),
				})
			),
		}),
	}),
	className: string,
	style: objectOf(oneOfType([string, number])),
	locale: string,
};

HeroSection.defaultProps = {
	primaryTile: null,
	gallery: null,
	otherTiles: null,
	className: "",
	style: {},
	locale: null,
};

export default HeroSection;
export { HeroSection };
